/* Intercom bus client — the webapp half of docs/imp-docs/intercom-bus/INTERCOM-BUS.md.

   A small module, not a component (PLAN wifi-change-webapp §1.3). One function does
   the whole exchange: write a request into `deviceManagement/{familyID}/hearthInbox`,
   watch `hearthOutbox` for responses matching our request id, order them by `at`,
   surface `progress` as they land, settle on `result`/`error`, delete what we
   consumed, and time out per endpoint.

   Contract obligations implemented here (§7):
     1. Online-ness is the CALLER's job — check the ping window before sending; there
        is no queue and a request to an offline device just waits for its timeout.
     2. Responses are matched by `replyTo`; request ids are per-session UUIDs, so
        another tab's responses can never match ours.
     3. Multiple responses are ordered by `at`; `result`/`error` complete the exchange.
     4. Timeouts are per endpoint (INTERCOM_TIMEOUTS_MS below).
     5. On timeout the caller asks AGAIN UNDER A NEW ID (just call send again) — there
        is no resumption and no correlation between the two attempts. The abandoned
        request stays in the inbox and may still be handled; the device drains oldest
        first, so the newer request is the one whose effect stands.
     6. Consumed response documents are deleted.
     7. Cancelling = deleting our own request (the returned `cancel`).

   Surviving a reload (PLAN §1.3): `sendIntercomRequest` returns the request `id` so a
   caller can persist it (sessionStorage, next to the session id) and, on remount,
   re-attach to the still-running exchange with [attachIntercomRequest] — which only
   listens, settling on the response already waiting in the outbox. Callers clear the
   persisted id when the exchange ends.

   §10: `progress` cannot drive a live progress bar for a WiFi change — the device is
   unreachable mid-switch and its progress writes flush together on reconnect. Treat
   the `onProgress` stream as a HISTORY rendered when the result lands, not a feed. */

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

export const INTERCOM_ENVELOPE_VERSION = 1;

/* There is deliberately NO capability gate on the webapp side (decision, 19 Aug):
   devices auto-update as soon as they are online, so the window in which a Hearth
   runs a pre-bus build is short, and the worst case is an exchange that times out.
   What carries that window is the page's failure copy — kept GENERIC (a secure
   connection could not be set up; try again in a little while), because the page
   cannot distinguish an old build from a transient failure or missing rules, and it
   must not assert a cause it cannot know, blame the Hearth, or suggest a reload. */

/* Per-endpoint timeouts (contract §7.1).

   The 150 s change.credentials figure is DERIVED FROM DEVICE CONSTANTS — the join
   engine's own budget in tvapp/HigloProto01/app/src/main/java/com/pommelhorse/
   higloproto/settings/AppConstants.kt:
     wifiScanTimeoutMs (5 s)
     + wifiConnectionMaxRetries (3) × wifiConnectionTimeoutMs (20 s)
     + 2 × wifiConnectionRetryDelayMs (2 s) + 2 × wifiAuthRetryDelayMs (3 s)
     + confirmInternetTimeoutMs (20 s)
     ≈ 95 s of joining, plus rollback/reconnect margin.
   If those constants change, THIS NUMBER SILENTLY BECOMES WRONG and we start telling
   customers a switch failed while it is still running — re-derive it from the device
   constants, which are now the ONLY place this budget is expressed. */
export const INTERCOM_TIMEOUTS_MS = {
  'security/generate.encryption.keys': 30000,
  'wifi/get.availableSSIDs': 30000,
  'wifi/get.currentSSID': 30000,
  'wifi/get.RSSI': 30000,
  'wifi/change.credentials': 150000,
};
const DEFAULT_TIMEOUT_MS = 30000;

/* The device answered `error` (contract §5.1 reasons: unknownTopic, unknownKind,
   unsupportedVersion, malformed, keyUnknown, internal). */
export class IntercomError extends Error {
  constructor(reason, payload) {
    super(`intercom error: ${reason}`);
    this.name = 'IntercomError';
    this.reason = reason || 'unknown';
    this.payload = payload || {};
  }
}

/* No terminal response inside the endpoint's window. NOT the same as failure — the
   device may be offline mid-work and the change may still happen (§10). The UI must
   render this distinctly from both success and failure (PLAN §4.4). */
export class IntercomTimeoutError extends Error {
  constructor(endpoint, timeoutMs) {
    super(`intercom timeout: no answer from ${endpoint} within ${timeoutMs} ms`);
    this.name = 'IntercomTimeoutError';
    this.endpoint = endpoint;
    this.timeoutMs = timeoutMs;
  }
}

function randomUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for very old browsers — uniqueness is all we need here.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* Session id — `webapp-<uuid>`, the envelope's `from`. Held in sessionStorage so a
   page RELOAD keeps the same identity (contract §3.3) while two tabs of the same
   customer get different ids. Reload recovery of a specific exchange is the CALLER's
   job: persist the id `sendIntercomRequest` returns and re-attach with
   `attachIntercomRequest` on remount. */
const SESSION_STORAGE_KEY = 'higloIntercomSessionId';

export function getIntercomSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = `webapp-${randomUUID()}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    // sessionStorage unavailable (privacy mode) — a per-load id still works, it just
    // won't survive a reload.
    if (!getIntercomSessionId.fallback) {
      getIntercomSessionId.fallback = `webapp-${randomUUID()}`;
    }
    return getIntercomSessionId.fallback;
  }
}

function atMillis(data) {
  const at = data && data.at;
  if (!at) return Number.MAX_SAFE_INTEGER; // unresolved timestamp sorts last
  if (typeof at.toMillis === 'function') return at.toMillis();
  return Number.MAX_SAFE_INTEGER;
}

/* Shared response-side machinery: watch the outbox for replyTo === requestId, order
   by `at`, feed progress, settle on the first terminal, delete what was consumed.
   Used by both send (fresh exchange) and attach (reload recovery). */
function listenForResponses(familyID, requestId, endpoint, timeoutMs, onProgress, hooks) {
  const outboxRef = collection(db, 'deviceManagement', familyID, 'hearthOutbox');

  let unsubscribe = null;
  let timer = null;
  let settled = false;

  const promise = new Promise((resolve, reject) => {
    const progressHistory = [];
    const consumed = new Set();

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      fn(value);
    };
    // Lets the caller fail the exchange from outside the listener (e.g. the request
    // write itself was rejected — nothing will ever answer it).
    hooks.fail = (err) => finish(reject, err);

    unsubscribe = onSnapshot(
      query(outboxRef, where('replyTo', '==', requestId)),
      (snap) => {
        // Order responses by `at` (contract §7.3) so a progress+result batch that
        // flushed together lands in the order the device wrote it.
        const docs = snap.docs
          .filter((d) => !consumed.has(d.id) && !d.metadata.hasPendingWrites)
          .sort((a, b) => atMillis(a.data()) - atMillis(b.data()));
        for (const d of docs) {
          if (settled) break;
          consumed.add(d.id);
          const data = d.data();
          // §7.6: delete what we have consumed. Fire-and-forget — a failed delete
          // just leaves a doc for the next boot clear.
          deleteDoc(d.ref).catch(() => {});
          if (data.kind === 'progress') {
            progressHistory.push(data);
            if (onProgress) {
              try { onProgress(data); } catch (e) { /* caller's problem */ }
            }
          } else if (data.kind === 'result') {
            finish(resolve, { payload: data, progress: progressHistory });
          } else if (data.kind === 'error') {
            finish(reject, new IntercomError(data.reason, data));
          }
          // Unknown response kinds from a newer device: ignored — additive change.
        }
      },
      (err) => finish(reject, err),
    );

    timer = setTimeout(() => {
      // §7.5: abandon. The caller retries by calling send again — a NEW id, a fresh
      // independent request, exactly as a timed-out HTTP call is re-issued. We do
      // NOT delete the abandoned request: it stays deliberately (someone asked for
      // it) and the device's oldest-first drain makes the newer request's effect the
      // one that stands.
      finish(reject, new IntercomTimeoutError(endpoint, timeoutMs));
    }, timeoutMs);
  });

  return {
    promise,
    stop: () => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    },
  };
}

/**
 * One request/response exchange.
 *
 * @param {string} familyID  target Hearth
 * @param {string} topic     e.g. 'wifi'
 * @param {string} kind      e.g. 'get.currentSSID'
 * @param {object} payload   kind-specific request fields (flat, merged into the doc)
 * @param {object} [options]
 * @param {number} [options.timeoutMs]   override the per-endpoint default
 * @param {function} [options.onProgress] called with each `progress` payload as it
 *                                        arrives (bunches after reconnect — history,
 *                                        not a feed)
 * @returns {{id: string, promise: Promise<{payload: object, progress: object[]}>, cancel: function, detach: function}}
 *   `id` is the request id — persist it to survive a reload (see attachIntercomRequest).
 *   `promise` resolves with the `result` payload (+ accumulated progress history),
 *   rejects with IntercomError / IntercomTimeoutError / a Firestore write error.
 *   `cancel` implements §7.7: deletes our own request; if the device has not picked
 *   it up, it never happens — if it has, the request is already gone (no-op).
 *   `detach` stops listening WITHOUT withdrawing the request — the exchange keeps
 *   running on the device; re-attach later with attachIntercomRequest. After detach
 *   the promise never settles.
 */
export function sendIntercomRequest(familyID, topic, kind, payload = {}, options = {}) {
  const endpoint = `${topic}/${kind}`;
  const timeoutMs = options.timeoutMs || INTERCOM_TIMEOUTS_MS[endpoint] || DEFAULT_TIMEOUT_MS;
  const id = randomUUID();
  const requestRef = doc(db, 'deviceManagement', familyID, 'hearthInbox', id);

  // Listener FIRST, then the write — a fast answer must not slip through the gap.
  const hooks = {};
  const listener = listenForResponses(
    familyID, id, endpoint, timeoutMs, options.onProgress, hooks,
  );

  setDoc(requestRef, {
    v: INTERCOM_ENVELOPE_VERSION,
    id,
    topic,
    kind,
    from: getIntercomSessionId(),
    at: serverTimestamp(),
    ...payload,
  }).catch((err) => {
    // The request never landed — nothing will ever answer it; fail immediately
    // rather than waiting out the timeout.
    if (hooks.fail) hooks.fail(err);
  });

  const cancel = () => {
    listener.stop();
    // §7.7: cancel by deleting our own request. Best-effort — if the device already
    // picked it up the document is gone and this is a no-op.
    deleteDoc(requestRef).catch(() => {});
  };

  return { id, promise: listener.promise, cancel, detach: listener.stop };
}

/**
 * Re-attach to an exchange this session already sent — the reload-recovery half of
 * PLAN §1.3. Listens only (never writes a request): if the terminal response is
 * already waiting in the outbox it settles immediately; otherwise it waits out
 * [timeoutMs] exactly like a fresh send.
 *
 * @returns {{promise: Promise, stop: function}} `stop` detaches without deleting
 *   anything — the exchange is deliberately left running (used on unmount when the
 *   caller wants to keep the persisted id alive).
 */
export function attachIntercomRequest(familyID, requestId, options = {}) {
  const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
  const listener = listenForResponses(
    familyID, requestId, options.endpointLabel || 'reattached', timeoutMs,
    options.onProgress, {},
  );
  return { promise: listener.promise, stop: listener.stop };
}
